import styleItemUtils from 'utils/style_item';
import { IDynamicLayoutStyleProps } from './dynamic_layout_style';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useContainerDimensions } from 'hooks/useContainerDimension';
import { useAppDispatch } from '../../redux/store';
import { setTrackingStyle } from '../../redux/analytic/analytic.slice';
import { animated, useSpring } from '@react-spring/web';

const CanvasLayoutStyle = ({
    heightContent,
    items,
    style,
    setShowDynamicItemDetail,
    onSetActiveItem,
    isInPdpWidget,
    setMetaPadding,
    handleShopNow,
}: IDynamicLayoutStyleProps) => {
    const [scale, setScale] = useState<number>(1);
    const combinedImageSize = useMemo(() => {
        return styleItemUtils.getStyleImageSize(style);
    }, [style]);
    const { width, height } = combinedImageSize;
    const containerRef = useRef<HTMLDivElement>(null);
    const { width: containerWidth, height: containerHeight } = useContainerDimensions(containerRef);
    const dispatch = useAppDispatch();
    const animationProps = useSpring({
        from: { opacity: 0 },
        to: { opacity: 1 },
        leave: { opacity: 0 },
        delay: 500,
    });

    const handleItemClick = (e, itemId: string, item) => {
        if (isInPdpWidget) {
            if (handleShopNow) {
                handleShopNow({ ...style, itemTaffiId: item?.taffiId, itemUrl: item?.url });
                e.stopPropagation();
            }
        }
        if (style) {
            dispatch(setTrackingStyle(style));
        }
        if (items) {
            const index = items.findIndex((i) => i.id === itemId);
            let item = items[index].data ?? items[index];
            if (style && style.imageJson) {
                const selectedImages = styleItemUtils.getSelectedImages(item, style.imageJson);
                item = { ...item, images: selectedImages };
            }
            setShowDynamicItemDetail && setShowDynamicItemDetail(item);
            onSetActiveItem && onSetActiveItem(index);
        }
    };

    useEffect(() => {
        const scaleDown: number[] = [];
        const scaleUp: number[] = [];
        if (containerWidth) {
            if (width > containerWidth) {
                scaleDown.push(containerWidth / width);
            } else {
                scaleUp.push(containerWidth / width);
            }
        }
        if (containerHeight) {
            if (height > containerHeight) {
                scaleDown.push(containerHeight / height);
            } else {
                scaleUp.push(containerHeight / height);
            }
        }
        if (scaleDown.length > 0) {
            setScale(Math.min(...scaleDown));
        } else if (scaleUp.length > 0) {
            setScale(Math.min(...scaleUp));
        }
    }, [width, height, containerWidth, containerHeight]);
    return (
        <div
            ref={containerRef}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: heightContent ? heightContent : 'auto',
                backgroundColor: '#FFFFFF',
                borderRadius: '12px',
            }}
        >
            <div
                style={{
                    width: width * scale,
                    height: height * scale,
                    maxWidth: 1024,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {style?.imageJson?.map((itemJson: any) => {
                    if (itemJson?.type === 'text') {
                        const top = itemJson.top * scale;
                        const left = itemJson.left * scale;
                        const fontSize = itemJson.fontSize ? itemJson.fontSize * scale : 12;
                        return (
                            <div
                                key={'text_' + itemJson.textId}
                                style={{
                                    position: 'absolute',
                                    top: top,
                                    left: left,
                                    transform: `rotate(${itemJson.angle}deg)`,
                                    pointerEvents: 'none',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: fontSize,
                                        color: itemJson.fontColor,
                                        fontFamily: itemJson.fontFamily,
                                        whiteSpace: 'pre-wrap',
                                        overflow: 'hidden',
                                        width: itemJson.width * itemJson.scaleX * scale,
                                        height: itemJson.height * itemJson.scaleY * scale,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {itemJson.text}
                                </div>
                            </div>
                        );
                    }
                    const imageWidth = itemJson.width * itemJson.scaleX * scale;
                    const imageHeight = itemJson.height * itemJson.scaleY * scale;
                    const matchedImage = itemJson.url;
                    let item = items.find((i) => i.id === itemJson.itemId);
                    if (item?.hasOwnProperty('data')) {
                        item = item.data;
                    }
                    const isCanvasLayout = true;
                    const imageDetail = styleItemUtils.getImageMetadataDetail(
                        item,
                        [],
                        matchedImage,
                        isCanvasLayout,
                        itemJson,
                    );
                    if (imageDetail) {
                        const { width, height, xmin, xmax, ymin, ymax } = imageDetail;
                        let additionalScaleX = 1;
                        let additionalScaleY = 1;
                        if (itemJson.width !== width && itemJson.heigth !== height) {
                            additionalScaleX = itemJson.width / width;
                            additionalScaleY = itemJson.height / height;
                        }
                        const top = ymin * itemJson.scaleY * scale * additionalScaleY;
                        const left = xmin * itemJson.scaleX * scale * additionalScaleX;
                        return (
                            <div
                                key={'imageBox_' + itemJson.itemId}
                                style={{
                                    position: 'absolute',
                                    width: imageWidth,
                                    height: imageHeight,
                                    top: itemJson.top * scale,
                                    left: itemJson.left * scale,
                                    transform: `rotate(${itemJson.angle}deg)`,
                                    pointerEvents: 'none',
                                }}
                            >
                                <div
                                    style={{ width: '100%', height: '100%', position: 'relative' }}
                                    data-additional-scale={additionalScaleX}
                                >
                                    <animated.div
                                        style={{
                                            width: (xmax - xmin) * itemJson.scaleX * scale * additionalScaleX,
                                            height: (ymax - ymin) * itemJson.scaleY * scale * additionalScaleY,
                                            position: 'absolute',
                                            top: top,
                                            left: left,
                                            pointerEvents: 'none',
                                            overflow: 'hidden',
                                            ...animationProps,
                                        }}
                                    >
                                        <img
                                            onClick={(e) => handleItemClick(e, itemJson.itemId, item)}
                                            alt={`item_` + itemJson.itemId}
                                            src={itemJson.imageCanvas}
                                            style={{
                                                width: imageWidth,
                                                height: imageHeight,
                                                transform: `translate(${-left}px, ${-top}px) rotate(${
                                                    itemJson.angle
                                                }deg)`,
                                                cursor: 'pointer',
                                                pointerEvents: 'auto',
                                            }}
                                        />
                                    </animated.div>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <animated.div
                            key={'imageBox_' + itemJson.itemId}
                            style={{
                                position: 'absolute',
                                width: imageWidth,
                                height: imageHeight,
                                top: itemJson.top * scale,
                                left: itemJson.left * scale,
                                transform: `rotate(${itemJson.angle}deg)`,
                                pointerEvents: 'none',
                                ...animationProps,
                            }}
                        >
                            <img
                                onClick={(e) => handleItemClick(e, itemJson.itemId, item)}
                                alt={`item_` + itemJson.itemId}
                                src={itemJson.imageCanvas}
                                style={{
                                    width: imageWidth,
                                    height: imageHeight,
                                    cursor: 'pointer',
                                    pointerEvents: 'auto',
                                }}
                            />
                        </animated.div>
                    );
                })}
            </div>
        </div>
    );
};

export default CanvasLayoutStyle;
